import { useState, useMemo } from 'react'
import { colors, formatNumber } from '../styles/tokens'
import { Button, Input, Select } from './FormControls'
import { calculateMiscellaneous } from '../lib/quantityEngine'
import { useDraftPersistence } from '../lib/useDraftPersistence'

const UNIT_MAP = { length: 'm', area: 'm²', volume: 'm³', weight: 'kg', count: 'No.' }

export default function MiscellaneousModulePanel({ scopeId, boqItemId, superseding, onClose, onError, saving, onSave }) {
  const draftKey = `draft:miscellaneous:${scopeId || boqItemId || 'unknown'}`
  const [draft, setDraft, clearDraft] = useDraftPersistence(draftKey, { items: [] })
  const initial = superseding ? { items: superseding.engineering_inputs?.items || [] } : draft
  const [items, setItemsRaw] = useState(initial.items)
  const setItems = (u) => setItemsRaw((p) => { const n = typeof u === 'function' ? u(p) : u; if (!superseding) setDraft((d) => ({ ...d, items: n })); return n })

  function addItem() { setItems((p) => [...p, { id: `I${Date.now()}`, description: '', measurementType: 'length', quantity: '' }]) }
  function updateItem(id, patch) { setItems((p) => p.map((i) => (i.id === id ? { ...i, ...patch } : i))) }
  function removeItem(id) { setItems((p) => p.filter((i) => i.id !== id)) }

  const itemsForCalc = useMemo(() => items.map((i) => ({ description: i.description, measurementType: i.measurementType, quantity: i.quantity === '' ? NaN : Number(i.quantity) })), [items])

  const result = useMemo(() => {
    if (itemsForCalc.length === 0) return { value: null, error: null }
    try { return { value: calculateMiscellaneous(itemsForCalc), error: null } } catch (e) { return { value: null, error: e.message } }
  }, [itemsForCalc])

  async function handleSave() {
    if (!result.value) return
    const succeeded = await onSave({ calculatedValueTon: result.value.finalQuantity, unit: result.value.finalUnit, geometrySnapshot: { items: [] }, engineeringInputs: { items }, breakdownSnapshot: result.value, takeoffItemIdForProvenance: null })
    if (succeeded !== false) clearDraft()
  }

  return (
    <div>
      <div style={{ padding: '8px 10px', background: '#FFF8E1', border: `1px solid #F5C542`, borderRadius: 6, fontSize: 11, color: '#7A5C00', marginBottom: 14 }}>
        ⚠ Controlled framework: Measurement Type is a locked category, and Quantity is a direct entry — there is no internal formula and no Rate/Value field here.
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <label style={{ fontSize: 11, fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase' }}>Items</label>
        <Button size="sm" variant="secondary" onClick={addItem}>+ Add Item</Button>
      </div>
      {items.map((i) => (
        <div key={i.id} style={{ border: `1px solid ${colors.border}`, borderRadius: 6, padding: 8, marginBottom: 8 }}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
            <Input placeholder="Description" value={i.description} onChange={(e) => updateItem(i.id, { description: e.target.value })} style={{ flex: 1, fontSize: 11 }} />
            <button onClick={() => removeItem(i.id)} style={{ background: 'none', border: 'none', color: colors.danger, cursor: 'pointer' }}>✕</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
            <L label="Measurement Type">
              <Select value={i.measurementType} onChange={(e) => updateItem(i.id, { measurementType: e.target.value })} style={{ fontSize: 11, width: '100%' }}>
                {Object.entries(UNIT_MAP).map(([k, u]) => <option key={k} value={k}>{k} ({u})</option>)}
              </Select>
            </L>
            <L label={`Quantity (${UNIT_MAP[i.measurementType]})`}><Input type="number" value={i.quantity} onChange={(e) => updateItem(i.id, { quantity: e.target.value })} style={{ fontSize: 11, width: '100%' }} /></L>
          </div>
        </div>
      ))}
      {result.value && (
        <div style={{ margin: '12px 0', padding: '10px 12px', background: '#F8FAFF', border: `1px solid ${colors.primary}`, borderRadius: 8, fontWeight: 700 }}>
          Total: {formatNumber(result.value.finalQuantity)} {UNIT_MAP[items[0]?.measurementType] || ''}
        </div>
      )}
      {result.error && <div style={{ fontSize: 12, color: colors.textMuted, fontStyle: 'italic', marginBottom: 14 }}>{result.error}</div>}
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        {!superseding && <button onClick={() => { if (window.confirm('Discard draft?')) { clearDraft(); onClose() } }} style={{ background: 'none', border: 'none', color: colors.danger, cursor: 'pointer', fontSize: 12 }}>Discard Draft</button>}
        <Button onClick={handleSave} disabled={!result.value || saving} style={{ marginLeft: 'auto' }}>{saving ? 'Saving…' : superseding ? 'Save New Version' : 'Save Output'}</Button>
      </div>
    </div>
  )
}
function L({ label, children }) { return <div><label style={{ display: 'block', fontSize: 10, color: colors.textMuted, marginBottom: 2 }}>{label}</label>{children}</div> }
